/*
  Third-party cart bridge.

  When "Disable theme cart drawer" is on (theme settings > Cart), this:
    1. stops the theme's #offcanvas-cart from ever opening, and
    2. routes every cart icon / cart link to the installed cart app instead.

  Config comes from window.themeThirdPartyCart, set in snippets/third-party-cart.liquid.
*/

;(function () {
  const config = window.themeThirdPartyCart || {}

  if (!config.disableThemeCart) return

  const CART_URL = (window.Shopify && window.Shopify.routes && window.Shopify.routes.cart_url) || '/cart'

  // Selectors for the theme's own cart triggers.
  const THEME_TRIGGERS = [
    '[href="#offcanvas-cart"]',
    '[data-bs-target="#offcanvas-cart"]',
    '[aria-controls="offcanvas-cart"]'
  ].join(',')

  // Known open APIs of common cart apps, tried in order. Each entry is
  // [is the app present?, open it] so a missing app is never counted as opened.
  const APP_OPENERS = [
    // UpCart
    [() => window.upcart && typeof window.upcart.openCart === 'function', () => window.upcart.openCart()],
    [() => window.UpCart && typeof window.UpCart.open === 'function', () => window.UpCart.open()],
    // Slide Cart Drawer (SlideCart HQ / AMP)
    [() => typeof window.SLIDECART_OPEN === 'function', () => window.SLIDECART_OPEN()],
    [() => window.slidecart && typeof window.slidecart.open === 'function', () => window.slidecart.open()],
    [() => window.SlideCartHQ && typeof window.SlideCartHQ.open === 'function', () => window.SlideCartHQ.open()],
    // Rebuy Smart Cart
    [() => window.Rebuy && window.Rebuy.SmartCart && typeof window.Rebuy.SmartCart.show === 'function', () => window.Rebuy.SmartCart.show()],
    [() => window.Rebuy && window.Rebuy.SmartCart && typeof window.Rebuy.SmartCart.open === 'function', () => window.Rebuy.SmartCart.open()],
    // Corner cart
    [() => window.cornerPlatform && window.cornerPlatform.cart && typeof window.cornerPlatform.cart.open === 'function', () => window.cornerPlatform.cart.open()],
    [() => window.CornerCart && typeof window.CornerCart.open === 'function', () => window.CornerCart.open()],
    // Monk / Zoorix / Vitals / SolidCart / generic
    [() => window.MonkCart && typeof window.MonkCart.open === 'function', () => window.MonkCart.open()],
    [() => window.zoorix && typeof window.zoorix.openCart === 'function', () => window.zoorix.openCart()],
    [() => window.Vitals && window.Vitals.cart && typeof window.Vitals.cart.open === 'function', () => window.Vitals.cart.open()],
    [() => window.SolidCart && typeof window.SolidCart.open === 'function', () => window.SolidCart.open()],
    [() => window.CartDrawer && typeof window.CartDrawer.open === 'function', () => window.CartDrawer.open()]
  ]

  // Trigger elements the apps render themselves, clicked as a fallback.
  const APP_TRIGGERS = [
    '#upcart-toggle',
    '.upcart-toggle',
    '[data-upcart-open]',
    '#slidecarthq-trigger',
    '[data-slidecart-open]',
    '[data-rebuy-smartcart-toggle]',
    '.rebuy-cart__flyout-trigger',
    '.corner-cart-trigger',
    '[data-corner-cart-open]',
    '.monk-cart-trigger'
  ]

  /* ------------------------------------------------------------------ *
   * Opening the app cart
   * ------------------------------------------------------------------ */

  function runCustomOpener () {
    if (!config.openJs) return false

    try {
      // eslint-disable-next-line no-new-func
      new Function(config.openJs)()
      return true
    } catch (error) {
      console.warn('[third-party-cart] custom open code failed:', error)
      return false
    }
  }

  function clickSelector (selector) {
    if (!selector) return false

    const elem = document.querySelector(selector)

    if (!elem) return false

    elem.click()
    return true
  }

  function runAppOpeners () {
    for (const [isPresent, open] of APP_OPENERS) {
      let present = false

      try {
        present = Boolean(isPresent())
      } catch (error) {
        present = false
      }

      if (!present) continue

      try {
        open()
        return true
      } catch (error) {
        console.warn('[third-party-cart] app opener threw:', error)
      }
    }

    return false
  }

  function openAppCart () {
    if (runCustomOpener()) return true
    if (clickSelector(config.triggerSelector)) return true
    if (runAppOpeners()) return true

    for (const selector of APP_TRIGGERS) {
      if (clickSelector(selector)) return true
    }

    return false
  }

  window.openThirdPartyCart = openAppCart

  /* ------------------------------------------------------------------ *
   * Keep the theme drawer shut
   * ------------------------------------------------------------------ */

  function isThemeCart (elem) {
    return Boolean(elem && elem.id === 'offcanvas-cart')
  }

  function blockBootstrapOffcanvas () {
    const bootstrap = window.bootstrap

    if (!bootstrap || !bootstrap.Offcanvas || bootstrap.Offcanvas.prototype.__themeCartBlocked) return false

    const proto = bootstrap.Offcanvas.prototype
    const show = proto.show
    const toggle = proto.toggle

    proto.show = function () {
      if (isThemeCart(this._element)) return
      return show.apply(this, arguments)
    }

    proto.toggle = function () {
      if (isThemeCart(this._element)) return
      return toggle.apply(this, arguments)
    }

    proto.__themeCartBlocked = true
    return true
  }

  // bootstrap.bundle is deferred; patch as soon as it lands.
  if (!blockBootstrapOffcanvas()) {
    const timer = setInterval(() => {
      if (blockBootstrapOffcanvas()) clearInterval(timer)
    }, 50)

    setTimeout(() => clearInterval(timer), 15000)
    document.addEventListener('DOMContentLoaded', blockBootstrapOffcanvas)
  }

  // The drawer must never be visible, even if something forces the class on.
  function hideThemeCart () {
    const drawer = document.getElementById('offcanvas-cart')

    if (!drawer) return

    drawer.classList.remove('show', 'showing')
    drawer.setAttribute('aria-hidden', 'true')

    // If the theme cart was the only thing that could have put a backdrop /
    // scroll lock on the page, clear them so the page never gets stuck.
    if (!document.querySelector('.offcanvas.show, .modal.show')) {
      document.querySelectorAll('.offcanvas-backdrop').forEach(backdrop => backdrop.remove())
      document.body.classList.remove('offcanvas-backdrop', 'overflow-hidden')
      document.body.style.removeProperty('overflow')
      document.body.style.removeProperty('padding-right')
    }
  }

  /* ------------------------------------------------------------------ *
   * Route theme cart triggers to the app cart
   * ------------------------------------------------------------------ */

  function neutralizeTriggers (root) {
    const scope = root && root.querySelectorAll ? root : document

    scope.querySelectorAll(THEME_TRIGGERS).forEach(trigger => {
      trigger.removeAttribute('data-bs-toggle')
      trigger.removeAttribute('data-bs-target')
      trigger.removeAttribute('aria-controls')

      if (trigger.tagName === 'A') trigger.setAttribute('href', CART_URL)

      trigger.classList.add('third-party-cart-trigger')
      trigger.dataset.thirdPartyCartTrigger = 'true'
    })
  }

  function isCartLink (elem) {
    const link = elem.closest('a[href]')

    if (!link) return false

    const href = link.getAttribute('href') || ''

    if (href === '#offcanvas-cart') return true

    try {
      const url = new URL(link.href, window.location.origin)
      return url.origin === window.location.origin && /\/cart\/?$/.test(url.pathname)
    } catch (error) {
      return false
    }
  }

  document.addEventListener('click', event => {
    // A cart app that hijacked the click already handled it.
    if (event.defaultPrevented) return

    const target = event.target

    if (!(target instanceof Element)) return

    const trigger = target.closest('[data-third-party-cart-trigger], ' + THEME_TRIGGERS)
    const isCart = trigger || isCartLink(target)

    if (!isCart) return

    if (openAppCart()) {
      event.preventDefault()
      event.stopPropagation()
      hideThemeCart()
    }
    // No app cart found: the link falls through to the /cart page rather than
    // silently doing nothing.
  }, true)

  /* ------------------------------------------------------------------ *
   * Wiring
   * ------------------------------------------------------------------ */

  // The theme's /cart page redirects to /?cart=true, which used to open the
  // theme drawer. Hand that intent to the app cart instead, retrying while a
  // late-booting app embed loads.
  function openFromCartUrl () {
    if (!new URLSearchParams(window.location.search).has('cart')) return

    let attempts = 0

    const timer = setInterval(() => {
      attempts += 1

      if (openAppCart() || attempts > 20) clearInterval(timer)
    }, 250)
  }

  function init () {
    neutralizeTriggers(document)
    hideThemeCart()
    openFromCartUrl()
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }

  // Header/cart markup is re-rendered by cart.js and by section rendering.
  const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === 1) neutralizeTriggers(node)
      }
    }
  })

  const startObserver = () => observer.observe(document.body, { childList: true, subtree: true })

  if (document.body) {
    startObserver()
  } else {
    document.addEventListener('DOMContentLoaded', startObserver)
  }

  window.addEventListener('kt.cart.reloaded', () => {
    neutralizeTriggers(document)
    hideThemeCart()
  })

  if (config.openOnAdd) {
    window.addEventListener('kt.cart.added', () => {
      hideThemeCart()
      setTimeout(openAppCart, 150)
    })
  } else {
    window.addEventListener('kt.cart.added', hideThemeCart)
  }

  window.addEventListener('kt.cart.changed', hideThemeCart)
})()
